/**
 * districtLookup.js
 *
 * Resolves a street address to the districts that actually govern it, using the
 * US Census Geocoder. Free, keyless, no rate-limit registration.
 *
 * Why Census and not Google Civic: the Civic Information API's
 * representativeInfoByAddress and representativeInfoByDivision endpoints were
 * turned down on 30 April 2025, so the route this project originally planned no
 * longer exists. Census returns the districts directly and is authoritative for
 * boundaries, which is what the matching engine actually needs.
 *
 * This module answers "which districts is this address in". It deliberately
 * does not answer "who represents it" - that join is made from district to
 * candidate records, keyed on identifiers rather than names.
 */

export const GEOCODER_URL = 'https://geocoding.geo.census.gov/geocoder/geographies/onelineaddress';

/**
 * Same-origin endpoint the browser calls instead of Census directly.
 *
 * Census answered HTTP 200 with no Access-Control-Allow-Origin header when
 * called from the staging origin on 2026-09-23, so the browser discarded the
 * response and onboarding showed "Failed to fetch". `api/district-lookup`
 * makes the same request server-side and returns the Census payload unchanged,
 * so everything below still parses the shape it was written against.
 */
export const LOOKUP_PATH = '/api/district-lookup';

// Census layer names. These carry a Congress number and a vintage year, so they
// change over time; reading them by prefix survives the next reapportionment
// without a code change, and an unknown layer degrades to null rather than
// throwing.
const LAYER_CONGRESS = /Congressional Districts$/;
const LAYER_UPPER = /State Legislative Districts - Upper$/;
const LAYER_LOWER = /State Legislative Districts - Lower$/;

/**
 * Builds the geocoder request URL for a one-line address.
 *
 * @param {string} address
 * @returns {string}
 */
export function buildGeocoderUrl(address) {
  const params = new URLSearchParams({
    address,
    benchmark: 'Public_AR_Current',
    vintage: 'Current_Current',
    format: 'json',
    layers: 'all',
  });
  // URLSearchParams encodes spaces as '+', which the geocoder accepts, but the
  // tests and logs read far better with %20 and it is equally valid.
  return `${GEOCODER_URL}?${params.toString().replace(/\+/g, '%20')}`;
}

/**
 * Builds the same-origin request URL. Only the address travels; the benchmark,
 * vintage and layers are fixed server-side so the caller cannot redirect the
 * proxy at another host.
 *
 * @param {string} address
 * @returns {string}
 */
export function buildLookupUrl(address) {
  const params = new URLSearchParams({ address });
  return `${LOOKUP_PATH}?${params.toString().replace(/\+/g, '%20')}`;
}

function findLayer(geographies, pattern) {
  const key = Object.keys(geographies ?? {}).find((name) => pattern.test(name));
  return key ? (geographies[key] ?? [])[0] ?? null : null;
}

/**
 * Census labels a chamber "State Senate District 14"; this application stores
 * "Senate District 14". Bridging that here keeps the mismatch in one place
 * instead of in every caller.
 */
function chamberLabel(entry) {
  if (!entry?.NAME) return null;
  return entry.NAME.replace(/^State\s+/, '');
}

function district(entry, label) {
  if (!entry) return null;
  return { label, number: entry.BASENAME ?? null, geoid: entry.GEOID ?? null };
}

/**
 * Extracts the districts from a single Census address match.
 *
 * Every layer is optional. The District of Columbia, for one, has an upper
 * chamber and no lower chamber - so a missing layer is a fact about the
 * jurisdiction, not an error.
 *
 * @param {object} match one entry from result.addressMatches
 * @returns {{matchedAddress: string|null, state: string|null, stateName: string|null,
 *            congressional: object|null, stateSenate: object|null, stateHouse: object|null,
 *            districtLabels: string[]}}
 */
export function extractDistricts(match) {
  const geographies = match?.geographies ?? {};

  const stateEntry = (geographies.States ?? [])[0] ?? null;
  const congressEntry = findLayer(geographies, LAYER_CONGRESS);
  const upperEntry = findLayer(geographies, LAYER_UPPER);
  const lowerEntry = findLayer(geographies, LAYER_LOWER);

  const congressional = district(congressEntry, congressEntry?.NAME ?? null);
  const stateSenate = district(upperEntry, chamberLabel(upperEntry));
  const stateHouse = district(lowerEntry, chamberLabel(lowerEntry));

  return {
    matchedAddress: match?.matchedAddress ?? null,
    state: stateEntry?.STUSAB ?? null,
    stateName: stateEntry?.NAME ?? null,
    congressional,
    stateSenate,
    stateHouse,
    // Display labels. NOT a safe join key: chamber naming varies by state -
    // "House District 78" in Virginia, "Assembly District 75" in New York,
    // "14th Suffolk District" in Massachusetts. Matching candidates on these
    // strings finds nothing outside Virginia-style states, and finding nothing
    // looks identical to a voter with no races.
    districtLabels: [stateSenate?.label, stateHouse?.label].filter(Boolean),
    // Join on these instead. GEOID is stable, numeric and uniform nationwide.
    districtGeoids: [stateSenate?.geoid, stateHouse?.geoid, congressional?.geoid].filter(Boolean),
  };
}

/**
 * Looks up the districts for an address.
 *
 * A voter typing a typo gets a graceful, explained result - `found: false` with
 * a reason - because that is a normal outcome, not a fault. A transport failure
 * or a malformed payload throws, because reporting "no districts" when the
 * service is simply down would quietly show a voter the wrong ballot.
 *
 * Requests go to the same-origin proxy by default. `urlBuilder` lets a
 * server-side caller, which has no CORS restriction, target Census directly
 * with `buildGeocoderUrl`.
 *
 * @param {string} address
 * @param {{fetchImpl?: Function, urlBuilder?: Function}} [options]
 * @returns {Promise<{found: boolean, districts: object|null, matchedAddress?: string, reason?: string, candidates?: Array}>}
 */
export async function lookupDistricts(address, { fetchImpl = fetch, urlBuilder = buildLookupUrl, signal } = {}) {
  if (!address || !String(address).trim()) {
    return { found: false, districts: null, reason: 'No address supplied.' };
  }

  const res = await fetchImpl(urlBuilder(String(address).trim()), { signal });
  if (!res.ok) {
    const messages = {
      400: 'Check the address and try again.',
      502: 'The address service is temporarily unavailable. Try again or pick your district manually.',
      503: 'The address service is temporarily unavailable. Try again or pick your district manually.',
      504: 'The address service took too long to respond. Try again or pick your district manually.',
    };
    throw new Error(messages[res.status] || 'Address lookup is unavailable. Try again or pick your district manually.');
  }

  const payload = await res.json();
  const matches = payload?.result?.addressMatches;
  if (!Array.isArray(matches)) {
    throw new Error('Address lookup returned an unexpected payload shape');
  }

  if (matches.length === 0) {
    return {
      found: false,
      districts: null,
      reason: 'No match for that address. Check the street number, city and ZIP.',
    };
  }

  if (matches.length > 1) {
    // Picking the first would silently choose one of several real places and
    // could show a voter another district's ballot.
    return {
      found: false,
      districts: null,
      reason: 'That address is ambiguous. Please choose the intended address.',
      candidates: matches.map((m) => ({ matchedAddress: m.matchedAddress, districts: extractDistricts(m) })),
    };
  }

  const districts = extractDistricts(matches[0]);
  return { found: true, districts, matchedAddress: districts.matchedAddress };
}
